/**
 * Routes, per BUILD_SPEC section 1.
 *
 * `/` redirects to the role's landing screen — manager to Reports, processor to
 * Repair overview, mechanic to the queue, viewer to the Status board, requester to
 * Mobile requests. The role decides; the persona is only a label.
 *
 * Every screen is wrapped in `RequireCapability` where the permission matrix says
 * so. That is the **chrome** half of enforcement: the API re-checks every route, so
 * a user who types a URL directly gets a 403 body rather than data. Hiding a tab
 * has never been a security boundary and is not treated as one here.
 */
import { Suspense, lazy } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { isRequesterOnly, type Capabilities } from "@truckbuddy/contract";
import { AppShell, useAppSession } from "./lib/shell";
import { Loading, PermissionDenied } from "./components/states";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { RequireTablet } from "./components/RequireTablet";
import { DailyClockGate } from "./components/DailyClockGate";
// `useIsCompact()`, not a hand-written `useMediaQuery("(max-width: 699px)")`.
// That hook exists precisely so the number lives in one place, and its own
// comment says so -- but it had NO callers, and these two lines were the
// duplication it was written to prevent.
import { useIsCompact } from "./lib/useIsCompact";
import { useDocumentTitle } from "./lib/document-title";
import { landingScreenFor } from "./lib/landing-screen";
import { SCREEN_LOADING_LABEL, preloadableScreen } from "./lib/preloadable-screen";

/*
 * ── Route-level code splitting (TRBUD-242) ─────────────────────────
 *
 * Measured before this change: the app chunk was 650 KB and Lighthouse
 * reported `unused-javascript` at **87% of it wasted on the queue** -- a
 * mechanic opening `/work-orders` downloaded, parsed and compiled Reports,
 * Configuration, the six Settings sub-pages, the report builder and the
 * invoice screens, and then used none of them. `vite.config.ts` already
 * named this as the next step and said what it needed: "Suspense boundaries
 * in App.tsx".
 *
 * ── The queue and the work order are lazy TOO, since TRBUD-295 ──────
 * TRBUD-242 kept `QueueScreen` and `WorkOrderScreen` eager: nearly every role
 * lands on one of them, and a lazy landing screen puts a chunk fetch in front
 * of its first paint. That was the right call at the time, and the cost of it
 * then grew out of sight. Those two screens and everything they import --
 * every dialog either can open, itemized tickets, the queue's three row
 * layouts, the vendor form inside the new-work-order dialog -- ride in the
 * ENTRY chunk, which every signed-in page downloads and executes before it can
 * paint anything. Between 10 and 24 September it grew from 151 KB to 207 KB
 * (39 KB to 54 KB gzip), so Settings, Permissions, Reports and the not-found
 * page were all paying for two screens they never render. Measured against
 * the 10 September build in alternating runs, same seed and machine, the
 * phone-preset LCP moved about 0.2 s later on almost every signed-in page,
 * including ones that render nothing but the shell -- which no per-screen
 * change can do. See docs/lighthouse/2026-09-25-follow-up.md.
 *
 * So they now get chunks of their own too, and the round trip TRBUD-242 was
 * right to worry about is kept off the critical path a different way. The
 * BUILD writes a small script into index.html (vite.config.ts,
 * lib/work-screen-preload.ts) that adds modulepreload links for the screen the
 * URL lands on, so its chunks download WITH the entry rather than after it --
 * measured, that is what separates this from a plain lazy() route, which cost
 * the queue about 0.4 s of phone-preset LCP. `preloadLandingScreen` below then
 * evaluates that chunk the moment this module runs, and is the whole preload
 * under the dev server, which has no chunks. `/` means the queue, because that
 * is where most roles land; a manager bound for Reports pays for a chunk that
 * used to be in his entry bundle anyway, so nobody is worse off than before.
 * And because the two are the pair a mechanic moves between all day, the first
 * pointer, key or touch fetches both (`preloadWorkScreensOnFirstInteraction`),
 * so moving between them stays instant without either being downloaded before
 * the first paint of any other screen.
 *
 * Everything else is reached by a deliberate click, at which point a small
 * chunk fetch is cheaper than having carried it since boot. The `Suspense`
 * boundary sits INSIDE `ErrorBoundary` and outside `Routes`, so a chunk that
 * fails to load (a deploy that removed it mid-session) surfaces as the
 * boundary's error rather than as a blank page, and the nav and tab bar keep
 * working either way.
 *
 * Do not "tidy" these into a single lazy import of a barrel file: a barrel
 * would put every screen back into one chunk and the split would silently
 * stop working while the code still looked split.
 */
/*
 * NOT `lazy()`, for these two: a lazy component suspends on its first render
 * even when its module is already loaded, and React then holds the screen back
 * for its 300 ms fallback throttle -- which would hand the time the preload won
 * straight back, and more. `preloadableScreen` renders a loaded module
 * synchronously instead; see lib/preloadable-screen.tsx for the measurement.
 */
const queue = preloadableScreen(async () => (await import("./screens/Queue")).QueueScreen);
const workOrder = preloadableScreen(async () => (await import("./screens/WorkOrder")).WorkOrderScreen);
const matrixReport = preloadableScreen(async () => (await import("./screens/ReportMatrix")).ReportMatrixScreen);
const ReportMatrixScreen = matrixReport.Screen;
const QueueScreen = queue.Screen;
const WorkOrderScreen = workOrder.Screen;

/**
 * Start the landing screen's chunk now, in parallel with the session read.
 * A failure is not reported here: the screen asks again when it renders, and
 * that attempt is the one whose failure reaches the error boundary.
 */
export function preloadLandingScreen(pathname: string): void {
  const landing = landingScreenFor(pathname);
  if (landing === "queue") void queue.preload();
  if (landing === "work-order") void workOrder.preload();
  if (landing === "report-matrix") void matrixReport.preload();
}

/**
 * Fetch both work screens on the first pointer, key or touch.
 *
 * After the first paint, never before it: that is when a download stops
 * competing with the screen being opened. A pointer or touch lands roughly a
 * hundred milliseconds before the click it becomes, which is usually the whole
 * fetch. One-shot; the listeners remove themselves.
 */
function preloadWorkScreensOnFirstInteraction(): void {
  const events = ["pointerdown", "keydown", "touchstart"] as const;
  const preload = () => {
    for (const type of events) window.removeEventListener(type, preload, true);
    void queue.preload();
    void workOrder.preload();
  };
  for (const type of events) window.addEventListener(type, preload, { capture: true, passive: true });
}

if (typeof window !== "undefined") {
  preloadLandingScreen(window.location.pathname);
  preloadWorkScreensOnFirstInteraction();
}

const ReviewScreen = lazy(async () => ({ default: (await import("./screens/Review")).ReviewScreen }));
const LogInvoiceScreen = lazy(async () => ({
  default: (await import("./screens/LogInvoice")).LogInvoiceScreen,
}));
const VendorsScreen = lazy(async () => ({ default: (await import("./screens/Vendors")).VendorsScreen }));
// TRBUD-466. No RequireTablet: the page reads on a phone, its rows just stop being links there.
const VendorCompletenessScreen = lazy(async () => ({
  default: (await import("./screens/vendors/VendorCompleteness")).VendorCompletenessScreen,
}));
const PartsScreen = lazy(async () => ({ default: (await import("./screens/Parts")).PartsScreen }));
const AssetsScreen = lazy(async () => ({ default: (await import("./screens/Assets")).AssetsScreen }));
const AssetRecordScreen = lazy(async () => ({
  default: (await import("./screens/AssetRecord")).AssetRecordScreen,
}));
const MainxExpenseReviewScreen = lazy(async () => ({ default: (await import("./screens/MainxExpenseReview")).MainxExpenseReviewScreen }));
const ReceiptAccuracyScreen = lazy(async () => ({ default: (await import("./screens/reports/ReceiptAccuracy")).ReceiptAccuracyScreen }));
/*
 * The Reports pages and Purchase orders, through their seams (TRBUD-439): the
 * route table below is final, and WP5 and WP6 fill these modules. One import()
 * per module, so the four Reports exports share one chunk.
 */
const reportPages = () => import("./screens/reports/ReportPages");
const ReportsLandingPage = lazy(async () => ({ default: (await reportPages()).ReportsLandingPage }));
const SpendCashPage = lazy(async () => ({ default: (await reportPages()).SpendCashPage }));
const WorkDowntimePage = lazy(async () => ({ default: (await reportPages()).WorkDowntimePage }));
const SavedReportsPage = lazy(async () => ({ default: (await reportPages()).SavedReportsPage }));
const PurchaseOrdersScreen = lazy(async () => ({ default: (await import("./screens/PurchaseOrders")).PurchaseOrdersScreen }));
const OverviewScreen = lazy(async () => ({ default: (await import("./screens/Overview")).OverviewScreen }));
const BoardScreen = lazy(async () => ({ default: (await import("./screens/Board")).BoardScreen }));
const DriverScreen = lazy(async () => ({ default: (await import("./screens/Driver")).DriverScreen }));
const RequestsInboxScreen = lazy(async () => ({
  default: (await import("./screens/RequestsInbox")).RequestsInboxScreen,
}));
const ConfigScreen = lazy(async () => ({ default: (await import("./screens/Config")).ConfigScreen }));
const SettingsScreen = lazy(async () => ({ default: (await import("./screens/Settings")).SettingsScreen }));
const TimeClockScreen = lazy(async () => ({ default: (await import("./screens/TimeClock")).TimeClockScreen }));
const AttendanceScreen = lazy(async () => ({ default: (await import("./screens/Attendance")).AttendanceScreen }));
const PermissionsScreen = lazy(async () => ({
  default: (await import("./screens/settings/Permissions")).PermissionsScreen,
}));
const CollectorsScreen = lazy(async () => ({
  default: (await import("./screens/settings/Collectors")).CollectorsScreen,
}));
const ConnectionsScreen = lazy(async () => ({
  default: (await import("./screens/settings/Connections")).ConnectionsScreen,
}));
const ApiConnectionsScreen = lazy(async () => ({
  default: (await import("./screens/settings/ApiConnections")).ApiConnectionsScreen,
}));
const LabourRatesScreen = lazy(async () => ({
  default: (await import("./screens/settings/LabourRates")).LabourRatesScreen,
}));
// TRBUD-459.
const PmBonusRateScreen = lazy(async () => ({
  default: (await import("./screens/settings/PmBonusRate")).PmBonusRateScreen,
}));
const DocumentScansScreen = lazy(async () => ({
  default: (await import("./screens/settings/DocumentScans")).DocumentScansScreen,
}));
const ReceiptReadingScreen = lazy(async () => ({
  default: (await import("./screens/settings/ReceiptReading")).ReceiptReadingScreen,
}));
const UsersScreen = lazy(async () => ({ default: (await import("./screens/settings/Users")).UsersScreen }));
const AppearanceScreen = lazy(async () => ({
  default: (await import("./screens/settings/Appearance")).AppearanceScreen,
}));
const SiteMaintenanceScreen = lazy(async () => ({
  default: (await import("./screens/settings/SiteMaintenance")).SiteMaintenanceScreen,
}));
const VendorMatchesScreen = lazy(async () => ({
  default: (await import("./screens/settings/VendorMatches")).VendorMatchesScreen,
}));

function RequireCapability({
  capability,
  what,
  children,
}: {
  capability: keyof Capabilities;
  what: string;
  children: React.ReactNode;
}) {
  const { can, role } = useAppSession();
  if (!can[capability]) return <PermissionDenied role={role} what={what} />;
  return <>{children}</>;
}

function AttendanceAccess() {
  const { realRoles, can, role } = useAppSession();
  return realRoles.some((held) => held === "admin" || held === "manager") && can.setLabourRate
    ? <AttendanceScreen /> : <PermissionDenied role={role} what="review attendance" />;
}

function LandingRedirect() {
  const { user, roles, can } = useAppSession();
  // Primary-role defaults can point at a wide-only screen. Compact landings are
  // operational and capability-backed, and use every held role.
  const compact = useIsCompact();
  if (!compact) return <Navigate to={user.defaultRoute} replace />;
  if (isRequesterOnly(roles)) return <Navigate to="/requests" replace />;
  if (roles.includes("processor")) return <Navigate to="/work-orders" replace />;
  if (can.viewStatusBoard && !can.advanceStages) return <Navigate to="/board" replace />;
  return <Navigate to="/work-orders" replace />;
}

/**
 * The work-orders queue, except for a driver (Eden, 22 October 2026).
 *
 * A requester-only person's queue scope includes the work raised from their own
 * requests, so its Completed and Cancelled tabs would list the finished history
 * their Requests screen no longer shows. No menu or link offers them this page,
 * so this is a safety net for a typed address, an old bookmark or a shared link:
 * they go back to Requests. A link to one work order (`/work-orders/:id`) still
 * opens that job, and anyone holding another role keeps the queue.
 */
function WorkOrdersRoute() {
  const { roles } = useAppSession();
  if (isRequesterOnly(roles)) return <Navigate to="/requests" replace />;
  return <QueueScreen />;
}

function RequestsRoute() {
  const { roles, role } = useAppSession();
  if (roles.some((held) => held === "admin" || held === "manager" || held === "processor")) {
    return <RequestsInboxScreen />;
  }
  if (roles.includes("requester")) return <DriverScreen />;
  return <PermissionDenied role={role} what="triage maintenance requests" />;
}

function RequireManagement({ children }: { children: React.ReactNode }) {
  const { roles, role } = useAppSession();
  if (!roles.some((held) => held === "admin" || held === "manager" || held === "processor")) {
    return <PermissionDenied role={role} what="view the repair overview" />;
  }
  return <>{children}</>;
}

function NotFound() {
  const location = useLocation();
  return (
    <div className="screen-body" style={{ paddingTop: "var(--space-6)" }}>
      <h4 className="section-attached">No screen at {location.pathname}</h4>
      <p className="text-muted" style={{ fontSize: "var(--tb-text-sm)" }}>
        Pick a page from the menu.
      </p>
    </div>
  );
}

const ReceiptViewerScreen = lazy(async () => ({
  default: (await import("./screens/review/ReceiptViewer")).ReceiptViewerScreen,
}));

// The pages an emailed link opens. Lazy, like every other screen, so the entry
// bundle every signed-in page loads does not carry two forms only a new or
// locked-out person ever sees.
const SetPasswordScreen = lazy(async () => ({
  default: (await import("./screens/SetPassword")).SetPassword,
}));
const ForgotPasswordScreen = lazy(async () => ({
  default: (await import("./screens/ForgotPassword")).ForgotPassword,
}));

/**
 * The pop-out receipt viewer, ABOVE `AppShell` — plan section 8, M12.
 *
 * Every other route is wrapped in the shell, which carries the nav. The viewer
 * is a second window beside the Review screen, so it gets none: it asks for the session itself and says one sentence when
 * there is none. `ThemeProvider` is in `main.tsx`, outside this, so the theme
 * tokens still apply. Branching here rather than in a `<Route>` is what keeps
 * the shell's hooks from running at all for this path.
 */
export function App() {
  const location = useLocation();
  /*
   * ── The emailed-link pages, also above the shell ────────────────────
   *
   * An invitation, a "forgot password" request and a reset link all open for a
   * person who is NOT signed in, so none of them may sit behind `AppShell`: its
   * session probe would put the sign-in form where the page should be. The path
   * is deliberately not under `/auth`, which the API owns and answers with JSON
   * 404s (`spa.ts`). The token these pages need rides in the URL fragment, so it
   * never reaches a server (`apps/api/src/mail/links.ts`).
   */
  if (location.pathname === "/set-password" || location.pathname === "/reset-password") {
    return (
      <ErrorBoundary what="this page" resetKey={location.pathname}>
        <Suspense fallback={<Loading label={SCREEN_LOADING_LABEL} />}>
          <SetPasswordScreen kind={location.pathname === "/set-password" ? "invite" : "reset"} />
        </Suspense>
      </ErrorBoundary>
    );
  }
  if (location.pathname === "/forgot-password") {
    return (
      <ErrorBoundary what="this page" resetKey={location.pathname}>
        <Suspense fallback={<Loading label={SCREEN_LOADING_LABEL} />}>
          <ForgotPasswordScreen />
        </Suspense>
      </ErrorBoundary>
    );
  }
  if (location.pathname === "/review/viewer") {
    return (
      <ErrorBoundary what="the receipt viewer" resetKey={location.pathname}>
        <Suspense fallback={<Loading label={SCREEN_LOADING_LABEL} />}>
          <ReceiptViewerScreen />
        </Suspense>
      </ErrorBoundary>
    );
  }
  return <ShellApp />;
}

function ShellApp() {
  const location = useLocation();
  /*
   * Named here rather than inside each screen: the title is a property of the
   * ROUTE, and twenty-three screens each setting their own is twenty-three
   * places for one to be forgotten. `titleForPath` is asserted directly in
   * `lib/document-title.test.ts`, so a new route that nobody named fails a
   * test instead of shipping a tab that says "TruckBuddy".
   */
  useDocumentTitle(location.pathname);
  return (
    <AppShell>
      <DailyClockGate>
      {/*
        Keyed on the pathname: a screen that throws is contained, the nav and tab
        bar keep working, and moving to another route resets the boundary. Without
        the key it latches and every subsequent screen shows the error too.
      */}
      <ErrorBoundary what="this screen" resetKey={location.pathname}>
        <div className="tb-screen" key={location.pathname}>
          {/*
            One boundary for every lazy route rather than one per route: the
            fallback is the same sentence in all of them, and a per-route
            `Suspense` would be twenty copies of it. It sits outside `Routes`
            because that is where the pending element is thrown from.
          */}
          <Suspense fallback={<Loading label={SCREEN_LOADING_LABEL} />}>
          <Routes>
        <Route path="/" element={<LandingRedirect />} />

        <Route path="/work-orders" element={<WorkOrdersRoute />} />
        <Route path="/work-orders/:id" element={<WorkOrderScreen />} />

        {/*
          `/review/new` BEFORE `/review`, and it is not an ordering accident that
          happens to work: react-router v6 ranks static segments above dynamic
          ones, so these two never compete -- but keeping the more specific one
          first is what makes the pair readable to whoever adds `/review/:itemId`
          next. Same capability on both: logging an invoice and confirming one are
          two halves of `manage_vendor_invoice`.
        */}
        <Route
          path="/review/new"
          element={
            <RequireCapability capability="confirmInvoice" what="log a vendor invoice">
              <LogInvoiceScreen />
            </RequireCapability>
          }
        />
        <Route
          path="/review"
          element={
            <RequireCapability capability="confirmInvoice" what="confirm parsed invoices">
              <ReviewScreen />
            </RequireCapability>
          }
        />
        <Route
          path="/vendors"
          element={
            <RequireCapability capability="viewVendors" what="view vendors">
              <RequireTablet screen="Vendors"><VendorsScreen /></RequireTablet>
            </RequireCapability>
          }
        />
        <Route
          path="/vendors/completeness"
          element={
            <RequireCapability capability="manageVendors" what="view vendor data completeness">
              <VendorCompletenessScreen />
            </RequireCapability>
          }
        />
        <Route
          path="/parts"
          element={
            <RequireCapability capability="viewParts" what="view parts">
              <PartsScreen />
            </RequireCapability>
          }
        />
        <Route
          path="/purchase-orders"
          element={
            <RequireCapability capability="viewParts" what="view purchase orders">
              <PurchaseOrdersScreen />
            </RequireCapability>
          }
        />
        <Route
          path="/assets"
          element={
            <RequireCapability capability="viewAssets" what="view assets">
              <AssetsScreen />
            </RequireCapability>
          }
        />
        <Route
          path="/assets/:id"
          element={
            <RequireCapability capability="viewAssets" what="view assets">
              <AssetRecordScreen />
            </RequireCapability>
          }
        />
        {/*
          The Reports pages (TRBUD-439): `viewReports`, and no `RequireTablet`
          here, because the pages are phone-capable (charts only, WP5). Declared
          before `/reports/:id`, which would otherwise read "spend" as a report
          id; react-router ranks static segments first anyway, and the order says
          so to whoever reads it. The builder routes keep their gate.
        */}
        <Route path="/reports/spend" element={<RequireCapability capability="viewReports" what="view reports"><SpendCashPage /></RequireCapability>} />
        <Route path="/reports/work" element={<RequireCapability capability="viewReports" what="view reports"><WorkDowntimePage /></RequireCapability>} />
        <Route path="/reports/saved" element={<RequireCapability capability="viewReports" what="view reports"><SavedReportsPage /></RequireCapability>} />
        <Route path="/reports/new" element={<RequireCapability capability="viewReports" what="run reports"><ReportMatrixScreen /></RequireCapability>} />
        <Route path="/reports/:id" element={<RequireCapability capability="viewReports" what="run reports"><ReportMatrixScreen /></RequireCapability>} />
        <Route
          path="/reports/mainx-review"
          element={<RequireCapability capability="reviewMainxExpenses" what="review MaintainX expenses"><MainxExpenseReviewScreen /></RequireCapability>}
        />
        {/* TRBUD-467: no menu item; opened from the Review queue and Settings → Receipt reading. */}
        <Route
          path="/reports/receipt-accuracy"
          element={<RequireCapability capability="viewReceiptAccuracy" what="see how accurately the AI reads receipts"><ReceiptAccuracyScreen /></RequireCapability>}
        />
        <Route
          path="/reports"
          element={
            <RequireCapability capability="viewReports" what="view reports">
              <ReportsLandingPage />
            </RequireCapability>
          }
        />
        <Route path="/overview" element={
          <RequireManagement>
            <OverviewScreen />
          </RequireManagement>
        } />
        <Route
          path="/board"
          element={
            <RequireCapability capability="viewStatusBoard" what="view the status board">
              <BoardScreen />
            </RequireCapability>
          }
        />
        <Route path="/requests" element={<RequestsRoute />} />
        <Route
          path="/config"
          element={
            <RequireCapability capability="configuration" what="change configuration">
              <RequireTablet screen="Configuration"><ConfigScreen /></RequireTablet>
            </RequireCapability>
          }
        />
        <Route path="/settings/connections/manage" element={
          <RequireCapability capability="configuration" what="manage API connections">
            <ApiConnectionsScreen />
          </RequireCapability>
        } />
        <Route path="/settings/connections" element={
          <RequireCapability capability="manageCollectors" what="monitor connections and bots">
            <ConnectionsScreen />
          </RequireCapability>
        } />

        {/*
          The hub is NOT capability-gated, and that is the difference between it
          and every route above. Its account group — theme, who you are signed in
          as — belongs to whoever is looking, and gating the whole page on
          `configuration` would make Settings an admin word for what is really two
          different things. The admin rows carry their own capability and vanish
          for everyone else; the page they lead to re-checks below, and the API
          re-checks again.
        */}
        <Route path="/settings" element={<SettingsScreen />} />
        <Route path="/time-clock" element={<TimeClockScreen />} />
        <Route path="/attendance" element={<AttendanceAccess />} />
        {/* Ungated for the same reason as the hub: a person's own colours and
            reading aids belong to whoever is looking. */}
        <Route path="/settings/appearance" element={<AppearanceScreen />} />
        <Route
          path="/settings/labour-rates"
          element={
            <RequireCapability capability="setLabourRate" what="set labour rates">
              <LabourRatesScreen />
            </RequireCapability>
          }
        />
        <Route
          path="/settings/pm-bonus"
          element={
            <RequireCapability capability="viewPmBonuses" what="see the PM bonus rate">
              <PmBonusRateScreen />
            </RequireCapability>
          }
        />
        <Route
          path="/settings/sb-driver-feed"
          // TRBUD-427: the feed is a card on Connections & bots now. Old links land on it.
          element={<Navigate to="/settings/connections#sb-driver-feed" replace />}
        />
        <Route
          path="/settings/collectors"
          element={
            <RequireCapability capability="manageCollectors" what="change collector switches">
              <CollectorsScreen />
            </RequireCapability>
          }
        />
        <Route
          path="/settings/document-scans"
          element={
            <RequireCapability capability="manageCollectors" what="see document scanning">
              <DocumentScansScreen />
            </RequireCapability>
          }
        />
        <Route
          path="/settings/receipt-reading"
          element={
            <RequireCapability capability="configuration" what="change receipt reading">
              <ReceiptReadingScreen />
            </RequireCapability>
          }
        />
        <Route
          path="/settings/permissions"
          element={
            <RequireCapability capability="configuration" what="change permissions">
              <PermissionsScreen />
            </RequireCapability>
          }
        />
        <Route
          path="/settings/vendor-matches"
          element={
            <RequireCapability capability="configuration" what="review vendor matches">
              <VendorMatchesScreen />
            </RequireCapability>
          }
        />
        <Route
          path="/settings/maintenance"
          element={
            <RequireCapability capability="manageSiteMaintenance" what="take TruckBuddy down for maintenance">
              <SiteMaintenanceScreen />
            </RequireCapability>
          }
        />
        <Route
          path="/settings/users"
          element={
            <RequireCapability capability="configuration" what="manage accounts">
              <UsersScreen />
            </RequireCapability>
          }
        />

        <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </div>
      </ErrorBoundary>
      </DailyClockGate>
    </AppShell>
  );
}
